import { useEffect, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { firestore as db } from "../../../firebase/firebase";
import toast from "react-hot-toast";
import notificationSound from "../../../../public/sounds/3.mp3";

// Define interfaces for the data structure
interface GeneralNotifications {
  id: string; // notification id - we can use it for delete notification
  to: string; // user id to which notification is sent
  type: string; // labtest or medicine order
  title: string; // notification title
  message: string; // notification message
  toId: string; // user id to which notification is sent
  status: string; // read or unread
  order_type: string;
  order_number: string;
  order_date: string;
  timestamp?: Date;
}

interface UseGeneralNotificationProps {
  toId: string;
  notify?: boolean;
}

const useGeneralNotification = ({
  toId,
  notify = true,
}: UseGeneralNotificationProps) => {
  const [notifications, setNotifications] = useState<GeneralNotifications[]>(
    []
  );
  const [error, setError] = useState<string>("");
  const isFirstLoad = useRef(true);

  const bellAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    bellAudioRef.current = new Audio(notificationSound);
  }, []);

  const updateNotificationStatus = async (id: string) => {
    try {
      const docRef = doc(db, "generalNotifications", id);
      await updateDoc(docRef, {
        status: "Read",
      });
      return true;
    } catch (err) {
      console.error("Error Notification status:", err);
      setError("Failed to update Notification status");
      return false;
    }
  };

  const sendNotifications = async (data: GeneralNotifications) => {
    console.log("\n\n DATA: ", data, "\n\n");
    const docRef = doc(collection(db, "generalNotifications"), data.id);
    await setDoc(
      docRef,
      {
        id: data.id, // notification id - we can use it for delete notification
        to: data.to, // user id to which notification is sent
        type: data.type, // notification type
        title: data.title, // notification title
        message: data.message, // notification message
        toId: data.toId, // user id to which notification is sent
        status: data.status, // read or unread
        orderType: data.order_type,
        orderNumber: data.order_number,
        orderDate: data.order_date,
        timestamp: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const deleteNotifications = async (id: string) => {
    await deleteDoc(doc(db, "generalNotifications", id));
  };

  useEffect(() => {
    if (!toId) return;

    const notificationsRef = collection(db, "generalNotifications");

    const unsubscribe = onSnapshot(
      query(notificationsRef, where("toId", "==", toId)),
      (snapshot) => {
        // Only process alerts if notify is enabled and it's not the first load
        if (notify && !isFirstLoad.current) {
          snapshot.docChanges().forEach((change) => {
            if (
              change.type === "added" &&
              change.doc.data().status === "Unread"
            ) {
              const data = change.doc.data() as GeneralNotifications;

              toast.success(`${data.title}\n${data.message}`);

              bellAudioRef.current?.play().catch(() => { });
            }
          });
        }

        isFirstLoad.current = false;

        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as GeneralNotifications[];

        // 📌 Sort:
        // 1. Unread first
        // 2. Latest first by timestamp
        const sortedData = data.sort((a, b) => {
          if (a.status !== b.status) {
            return a.status === "Unread" ? -1 : 1;
          }
          return (b.timestamp?.seconds ?? 0) - (a.timestamp?.seconds ?? 0);
        });

        setNotifications(sortedData);
        setError("");
      },
      (err) => {
        console.error("Error in real-time listener:", err);
        setError("Failed to sync with the database.");
      }
    );

    return () => unsubscribe();
  }, [toId, notify]);

  return {
    sendNotifications,
    notifications,
    error,
    updateNotificationStatus,
    deleteNotifications,
  };
};

export default useGeneralNotification;
